import api from "@core/plugins/api";
import { JavascriptHelpers, ArrayHelpers } from "@core/helpers";
import { DEMAND_TYPES } from "@core/constants";
import { RecruitmentCandidatesFiltersDTO, DeclareExtraMissingDTO, ReportCandidateDTO, RecruitedCandidateDTO } from "@core/dto";

// PERMANENT
const getExpiredRecruitmentCandidatesUrl = (demandId) => `/cdi-review/et/contacted-candidates/${demandId}`;
const getPermanentCandidatesUrl = (id, filters) => `/cdi-candidate/et/search-candidates/${id}?${filters}`;
const getReportPermanentCandidateReasonsUrl = () => `/cdi-review/et/reasons`;
const contactPermanentCandidateUrl = (demandId, candidateId) => `/api/et/offrecdi/${demandId}/profil/${candidateId}`;
const ignorePermanentCandidateUrl = (demandId, candidateId) => `/api/et/offrecdi/${demandId}/profil/${candidateId}`;
const reportPermanentCandidateUrl = () => `/cdi-review/et/report`;
const recruitedPermanentCandidateUrl = () => `/cdi-review/et/recruit-candidate`;
// TEMPORARY AE
const bookExtraAEUrl = (demandId, extraId) => `/api/et/book/extra/${extraId}/mission/${demandId}`;
const cancelFutureShiftsUrl = (demandId, extraId) => `/api/et/mission/annule/etab/${demandId}/${extraId}`;
const declareExtraMissingUrl = () => `/api/et/finalisation`;
const ignoreAECandidateUrl = (demandId, candidateId) => `/api/et/unset/extra/${candidateId}/mission/${demandId}`;
// TEMPORARY MER
const cancelExtraUrl = (demandId) => `/api/et/job/annule/${demandId}`;
const cancelRestartExtraUrl = (demandId) => `/api/et/job/annulerelance/${demandId}`;
const ignoreMERCandidateUrl = (demandId, candidateId) => `/api/et/unset/extra/${candidateId}/demande/${demandId}`;

export class RecruitmentCandidatesService {
  // #region PERMANENT
  static async getRecruitmentCandidates({ demandId, type, status, query, nb, page }) {
    try {
      const dto = new RecruitmentCandidatesFiltersDTO(query);
      const preparedFiltersData = dto.prepareFiltersData();
      const filters = JavascriptHelpers.getUrlFilters({
        ...preparedFiltersData,
        status,
        nb,
        page,
      });

      let url;
      switch (type.toUpperCase()) {
        case DEMAND_TYPES.CDI:
        case DEMAND_TYPES.SEASON:
          url = getPermanentCandidatesUrl(demandId, filters);
          break;
        default:
          throw new Error("not implemented demand type", type);
      }
      const { data } = await api.private.get(url);
      return data;
    } catch (error) {
      throw error;
    }
  }

  static async getReportCandidateReasons() {
    try {
      const { data } = await api.private.get(getReportPermanentCandidateReasonsUrl());
      return ArrayHelpers.ensureArray(data);
    } catch (error) {
      throw error;
    }
  }

  static async contactPermanentCandidate({ demandId, candidateId }) {
    try {
      const { data } = await api.private.put(contactPermanentCandidateUrl(demandId, candidateId));
      return data;
    } catch (error) {
      throw error;
    }
  }

  static async ignorePermanentCandidate({ demandId, candidateId }) {
    try {
      const { data } = await api.private.delete(ignorePermanentCandidateUrl(demandId, candidateId));
      return data;
    } catch (error) {
      throw error;
    }
  }

  static async reportPermanentCandidate({ candidateId, demandId, reasonId, comment }) {
    try {
      const dto = new ReportCandidateDTO({ candidateId, demandId, reasonId, comment });
      const requestData = dto.prepareReportCandidateData();
      const { data } = await api.private.post(reportPermanentCandidateUrl(), requestData);
      return data;
    } catch (error) {
      throw error;
    }
  }

  static async recruitedPermanentCandidate({ candidateId, demandId, recruited }) {
    try {
      const dto = new RecruitedCandidateDTO({ candidateId, demandId, recruited });
      const requestData = dto.prepareRecruitedCandidateData();
      const { data } = await api.private.post(recruitedPermanentCandidateUrl(), requestData);
      return data;
    } catch (error) {
      throw error;
    }
  }

  static async getExpiredRecruitmentCandidates({ demandId }) {
    try {
      const { data } = await api.private.get(getExpiredRecruitmentCandidatesUrl(demandId));
      const candidates = data.contacted_candidates;
      return ArrayHelpers.ensureArray(candidates);
    } catch (error) {
      throw error;
    }
  }
  // #endregion PERMANENT

  // #region TEMPORARY AE
  static async bookExtraAE({ recruitment, extraId }) {
    if (recruitment.isAE()) {
      try {
        const demandId = recruitment.getDemandId();
        const { data } = await api.private.put(bookExtraAEUrl(demandId, extraId));
        return data;
      } catch (error) {
        throw error;
      }
    } else {
      throw new Error("WRONG RECRUITMENT TYPE");
    }
  }

  static async cancelFutureShifts({ demandId, extraId }) {
    try {
      const { data } = await api.private.put(cancelFutureShiftsUrl(demandId, extraId));
      return data;
    } catch (error) {
      throw error;
    }
  }

  static async declareExtraMissing({ recruitment, id }) {
    try {
      let data;
      const dto = new DeclareExtraMissingDTO({ id });
      if (recruitment.isAE()) {
        data = dto.prepareAEData();
      }

      return api.private.post(declareExtraMissingUrl(), data);
    } catch (error) {
      throw error;
    }
  }

  static async ignoreAECandidate({ demandId, candidateId }) {
    try {
      const { data } = await api.private.post(ignoreAECandidateUrl(demandId, candidateId));
      return data;
    } catch (error) {
      throw error;
    }
  }
}
